// @flow
import * as React from 'react'
import { useState, useEffect, useContext, useCallback } from 'react'

import { saveToLocalStorage } from '~/core/utils/functions'

import cz from './cz.json'
import en from './en.json'
import de from './de.json'

const translations = { cz, en, de }

const isValidTranslation = (lang: string) => translations.hasOwnProperty(lang)

const getMessage = (dictionary: { [key: string]: any }, path: string) => {
  let accumulator: any = dictionary

  for (const part of path.split('.')) {
    if (accumulator[part]) {
      accumulator = accumulator[part]
    } else {
      return path
    }
  }

  return accumulator
}

type MsgVariablesType = {
  [key: string]: string | number
}

const buildMessage = (lang: string, msgPath: string, variables?: MsgVariablesType) => {
  const dictionary = translations[lang]
  let text = getMessage(dictionary, msgPath)

  if (typeof text === 'string' && variables && Object.keys(variables).length) {
    Object.entries(variables).forEach(([key, value]: [string, any]) => {
      text = text.replace(`{${key}}`, value)
    })
  }
  return text
}

type I18nContextState = {
  lang: string,
  setLanguage: (lang: string) => void
}

const I18nContext = React.createContext<I18nContextState>({})

type I18nContextProviderProps = {
  initialLanguage: string,
  children: React.Node
}

const I18nContextProvider = ({ initialLanguage, children }: I18nContextProviderProps) => {
  const [state, setState] = useState({ lang: isValidTranslation(initialLanguage) ? initialLanguage : 'cz' })

  // For debugging purposes only
  useEffect(() => {
    window.getI18nContext = key => state[key] || state
  }, [state])

  const setLanguage = lang => {
    if (isValidTranslation(lang)) {
      setState({ lang })
      saveToLocalStorage('lang', lang)
    }
  }

  return <I18nContext.Provider value={{ ...state, setLanguage }}>{children}</I18nContext.Provider>
}

const useI18n = () => {
  const context = useContext(I18nContext)
  const msg = useCallback(
    (msgPath: string, variables?: MsgVariablesType, defaultValue?: string) => {
      const message = buildMessage(context.lang, msgPath, variables)

      if (!message) {
        return defaultValue || msgPath
      }

      return message
    },
    [context.lang]
  )

  if (!context) {
    throw new Error('[I18nContext] useI18n must be used within I18nContextProvider')
  }

  return [msg, context.lang, context.setLanguage]
}

export { useI18n, I18nContextProvider }
